import React, { useState } from "react";
import { Upload, X, FileDown, Eye, CheckCircle2, AlertTriangle, XCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "@/lib/api";

// Oran rozet renkleri — backend export fill'leri ile aynı
const ORAN_BADGE = {
  "15": { bg: "bg-amber-100", bd: "border-amber-300", tx: "text-amber-900" },
  "25": { bg: "bg-orange-100", bd: "border-orange-300", tx: "text-orange-900" },
  "1.5": { bg: "bg-teal-100", bd: "border-teal-300", tx: "text-teal-900" },
  "2.5": { bg: "bg-violet-100", bd: "border-violet-300", tx: "text-violet-900" },
};

const STATUS_META = {
  ok:      { Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "GEÇERLİ" },
  warning: { Icon: AlertTriangle, cls: "text-amber-700 bg-amber-50 border-amber-200",    label: "UYARI" },
  error:   { Icon: XCircle,       cls: "text-rose-700 bg-rose-50 border-rose-200",       label: "HATA" },
};

export default function ImportModal({ onClose, onDone, onDownloadTemplate }) {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // { total, ok_count, warning_count, error_count, importable_count, rows }
  const [result, setResult] = useState(null);

  const resetAll = () => { setPreview(null); setResult(null); };

  const handlePreview = async () => {
    if (!file) { toast.error("Önce dosya seçin"); return; }
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/prim/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);
      setResult(null);
      if (res.data.error_count > 0) {
        toast.warning(`${res.data.error_count} satır içe aktarılamayacak`);
      } else {
        toast.success(`${res.data.importable_count} satır içe aktarıma hazır`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Önizleme başarısız");
    } finally { setPreviewing(false); }
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/prim/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.created > 0) toast.success(`${res.data.created} prim kaydı eklendi`);
      if (res.data.errors?.length > 0) toast.warning(`${res.data.errors.length} satırda uyarı`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarılamadı");
    } finally { setImporting(false); }
  };

  const inPreview = !!preview && !result;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div
        className={`bg-white rounded-xl shadow-xl border border-slate-200 w-full ${inPreview ? "max-w-5xl" : "max-w-xl"}`}
        data-testid="import-modal"
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Upload size={18} /> Toplu Prim İçe Aktar
            {inPreview && <span className="text-xs font-medium text-slate-500">· Önizleme (Dry-Run)</span>}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>

        {/* --- STEP 1: Upload --- */}
        {!inPreview && !result && (
          <div className="p-5 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <p className="font-semibold mb-1">Nasıl Çalışır?</p>
              <ol className="list-decimal list-inside space-y-0.5 text-xs">
                <li>Aşağıdaki şablonu indir ve kayıtları ekle</li>
                <li>Personel adlarını <strong>birebir aynı</strong> yaz (Ör: "Ahmet Yılmaz")</li>
                <li><strong>Varlık Şirketi</strong> → "Evet" veya "Hayır"</li>
                <li><strong>Şehir</strong> → "İçi" veya "Dışı" (oran otomatik atanır — manuel oran girmene gerek yok)</li>
                <li><strong>Önizle</strong> ile dosyayı kontrol et → sorun yoksa <strong>İçe Aktar</strong></li>
              </ol>
              <div className="mt-2 text-[11px] bg-amber-100/60 border border-amber-300 rounded px-2 py-1.5 font-mono">
                Hayır+İçi=<strong>%15</strong> · Hayır+Dışı=<strong>%25</strong> · Evet+İçi=<strong>%1,5</strong> · Evet+Dışı=<strong>%2,5</strong>
              </div>
            </div>

            <button
              data-testid="download-template-button"
              onClick={onDownloadTemplate}
              className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-3 rounded-lg font-semibold text-sm transition-colors"
            >
              <FileDown size={16} /> Şablonu İndir (.xlsx)
            </button>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
                Yüklenecek Dosya
              </label>
              <input
                data-testid="import-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setFile(e.target.files?.[0] || null); resetAll(); }}
                className="w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:font-semibold hover:file:bg-slate-800 cursor-pointer"
              />
              {file && (
                <div className="mt-2 text-xs text-slate-500">Seçili: {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
              <button
                data-testid="preview-import-button"
                onClick={handlePreview}
                disabled={!file || previewing}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <Eye size={14} />
                {previewing ? "Önizleniyor..." : "Önizle"}
              </button>
            </div>
          </div>
        )}

        {/* --- STEP 2: Preview result --- */}
        {inPreview && (
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <KpiTile label="Toplam Satır" value={preview.total} tone="slate" testid="preview-total" />
              <KpiTile label="Geçerli" value={preview.ok_count} tone="emerald" testid="preview-ok" />
              <KpiTile label="Uyarılı (İçe Aktarılacak)" value={preview.warning_count} tone="amber" testid="preview-warn" />
              <KpiTile label="Hatalı (Atlanacak)" value={preview.error_count} tone="rose" testid="preview-err" />
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-2 py-1.5 font-bold">#</th>
                    <th className="px-2 py-1.5 font-bold">Durum</th>
                    <th className="px-2 py-1.5 font-bold">Tarih</th>
                    <th className="px-2 py-1.5 font-bold">Dosya / Müvekkil</th>
                    <th className="px-2 py-1.5 font-bold">Varlık · Şehir</th>
                    <th className="px-2 py-1.5 font-bold text-center">Oran</th>
                    <th className="px-2 py-1.5 font-bold text-right">Vekalet</th>
                    <th className="px-2 py-1.5 font-bold text-right">Tahsilat</th>
                    <th className="px-2 py-1.5 font-bold text-right">Ödenecek</th>
                    <th className="px-2 py-1.5 font-bold">Giden</th>
                    <th className="px-2 py-1.5 font-bold">Uyarı/Hata</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {preview.rows.map((r) => {
                    const sm = STATUS_META[r.status] || STATUS_META.ok;
                    const SIcon = sm.Icon;
                    const oranKey = String(r.oran);
                    const ob = ORAN_BADGE[oranKey] || { bg: "bg-slate-100", bd: "border-slate-200", tx: "text-slate-700" };
                    return (
                      <tr key={r.row_num} data-testid={`preview-row-${r.row_num}`} className={r.status === "error" ? "bg-rose-50/40" : r.status === "warning" ? "bg-amber-50/40" : ""}>
                        <td className="px-2 py-1.5 font-mono text-slate-500">{r.row_num}</td>
                        <td className="px-2 py-1.5">
                          <span className={`inline-flex items-center gap-1 border rounded-full px-1.5 py-0.5 text-[10px] font-bold ${sm.cls}`}>
                            <SIcon size={10} /> {sm.label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-slate-700">{fmtDate(r.tarih) || "—"}</td>
                        <td className="px-2 py-1.5">
                          <div className="font-semibold text-slate-900 truncate max-w-[180px]" title={r.dosya_adi}>{r.dosya_adi || "—"}</div>
                          <div className="text-slate-500 text-[10px] truncate max-w-[180px]" title={r.alacakli_adi}>{r.alacakli_adi}</div>
                        </td>
                        <td className="px-2 py-1.5">
                          <div className="text-[10px]"><strong>{r.varlik_label}</strong></div>
                          <div className="text-[10px] text-slate-600">{r.sehir_label}</div>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`inline-block border rounded-md px-2 py-0.5 font-mono font-bold ${ob.bg} ${ob.bd} ${ob.tx}`}>
                            %{String(r.oran).replace(".", ",")}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-700">{(r.vekalet_ucreti || 0).toLocaleString("tr-TR")}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-slate-700">{(r.tahsilat_miktari || 0).toLocaleString("tr-TR")}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-800">{(r.odenecek_toplam || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ₺</td>
                        <td className="px-2 py-1.5">
                          {r.giden_count > 0 ? (
                            <span className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[10px] font-bold">{r.giden_count}× </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          {r.errors?.map((e, i) => (
                            <div key={`e${i}`} className="text-rose-700 text-[10px] flex items-start gap-1"><XCircle size={10} className="mt-0.5 shrink-0"/>{e}</div>
                          ))}
                          {r.warnings?.map((w, i) => (
                            <div key={`w${i}`} className="text-amber-700 text-[10px] flex items-start gap-1"><AlertTriangle size={10} className="mt-0.5 shrink-0"/>{w}</div>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {preview.error_count > 0 && (
              <div className="text-xs bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-rose-900">
                <strong>{preview.error_count}</strong> hatalı satır içe aktarılmayacak. Sadece <strong>{preview.importable_count}</strong> satır (geçerli + uyarılı) eklenecek.
              </div>
            )}

            <div className="flex justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={() => resetAll()}
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold inline-flex items-center gap-1.5"
                data-testid="back-to-upload"
              >
                <ArrowLeft size={14}/> Dosya Değiştir
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
                <button
                  data-testid="confirm-import-button"
                  onClick={handleImport}
                  disabled={importing || preview.importable_count === 0}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  <Upload size={14}/>
                  {importing ? "İçe aktarılıyor..." : `${preview.importable_count} Satırı İçe Aktar`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- STEP 3: Final result --- */}
        {result && (
          <div className="p-5 space-y-4">
            <div className={`rounded-lg p-3 text-sm border ${result.created > 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                {result.created > 0 ? <CheckCircle2 size={16} className="text-emerald-700"/> : <XCircle size={16} className="text-rose-700"/>}
                {result.created > 0 ? `${result.created} prim kaydı başarıyla eklendi` : "Hiç kayıt eklenemedi"}
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="font-semibold text-amber-700 mb-1">Notlar ({result.errors.length}):</div>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto space-y-0.5 text-slate-600">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                data-testid="close-import-done"
                onClick={result.created > 0 ? onDone : onClose}
                className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
              >
                {result.created > 0 ? "Kapat & Yenile" : "Kapat"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiTile({ label, value, tone, testid }) {
  const toneCls = {
    slate:   "bg-slate-50 border-slate-200 text-slate-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div className={`border rounded-md px-3 py-2 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xl font-extrabold font-mono">{value}</div>
    </div>
  );
}
